import axios from 'axios';
import qs from 'qs';
import Raven from 'raven-js';
import { currentToken, currentImpersonatorToken } from '~/common/auth';
import { logoutWithRedirectBack, getCoachPlatformApiHost } from '~/common/utils';

class CoachPlatformAPI {
  static uri(path) {
    const apiEndpoint = getCoachPlatformApiHost();
    return apiEndpoint + path;
  }

  static async headers() {
    return currentToken()
      .then(token => {
        let header = {
          'content-type': 'application/json',
          accept: 'application/json',
          'app-version': 'coach-app',
          authorization: 'Bearer ' + token,
        };

        let impersonatorToken = currentImpersonatorToken();
        if (impersonatorToken) {
          header = {
            ...header,
            'x-naluri-impersonator-token': impersonatorToken,
          };
        }

        return header;
      })
      .catch(err => {
        if (err.code === 'NotAuthorizedException') {
          // If refresh token, or current session is expired,
          // Logout and redirect users.
          logoutWithRedirectBack();
        }
      });
  }

  static async request(resources, params, opts = {}) {
    // If the user refresh token is expired, headers will be undefined.
    let headers = opts.headers ? opts.headers : await CoachPlatformAPI.headers();
    let endpoint = CoachPlatformAPI.uri(resources);

    let options = Object.assign(
      {},
      {
        url: endpoint,
        headers,
      },
      opts,
    );

    return axios(endpoint, options)
      .then(CoachPlatformAPI.handleResponse)
      .catch(e => {
        throw CoachPlatformAPI.handleError(e);
      });
  }

  static get(resources, params, opts = {}) {
    opts = Object.assign({}, opts, {
      method: 'GET',
      data: null,
    });

    resources = !params ? resources : resources + '?' + qs.stringify(params);
    return CoachPlatformAPI.request(resources, null, opts);
  }

  static post(resources, params, opts = {}) {
    opts = Object.assign({}, opts, {
      data: CoachPlatformAPI.serialize(params),
      method: 'POST',
    });

    return CoachPlatformAPI.request(resources, params, opts);
  }

  static formidable(resource, request, opts = {}) {
    let headers = CoachPlatformAPI.headers();
    delete headers['content-type'];
    delete headers['accept'];

    opts = {
      headers: { ...headers, 'content-type': 'multipart/form-data' },
      method: opts.method,
      data: request,
    };

    return CoachPlatformAPI.request(resource, request, opts);
  }

  static put(resources, params, opts = {}) {
    opts = Object.assign({}, opts, {
      data: CoachPlatformAPI.serialize(params),
      method: 'PUT',
    });

    return CoachPlatformAPI.request(resources, params, opts);
  }

  static delete(resources, params, opts = {}) {
    opts = Object.assign({}, opts, {
      data: CoachPlatformAPI.serialize(params),
      method: 'DELETE',
    });

    return CoachPlatformAPI.request(resources, params, opts);
  }

  static serialize(params) {
    return JSON.stringify(params);
  }

  static handleResponse(response) {
    const { status, data } = response;

    if (status < 200 && status > 300) {
      throw this.handleError(response);
    }
    return data;
  }

  static handleError(error) {
    const errs = error && error.response ? error.response.data : error;
    Raven.captureException(new Error(errs), {
      tags: {
        error_type: 'API Failed',
        error_url: error.request.responseURL + ':' + error.request.status,
      },
    });
    return errs;
  }
}

export default CoachPlatformAPI;


// WEBPACK FOOTER //
// ./src/common/api/coachPlatformAPI.js